import classNames from "classnames";
import { useState, useRef, useMemo } from "react";
import { Button, ButtonGroup } from "reactstrap";
import { Popover, Whisper } from "rsuite";
import _ from "underscore";

import ManageItemsDrawer from "components/Drawers/ManageItemsDrawer";
import CreateTableLayoutDrawer from "./CreateTableLayoutDrawer";
import LayoutsOptions from "./LayoutsOptions";
import useGlobals from "../../api/api-hooks/globals/useGlobal";
import CalendarLayoutDrawer from "./CalendarLayoutDrawer";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import { IsMobileAtom } from "../../App";
import { useAtomValue } from "jotai";

// Map with import information regarding Table,Cards,Boards view types.
export const layoutTypeMap = {
  Table: {
    name: "Table Layouts",
    itemKey: "TableLayouts",
    icon: "table",
    viewName: "Table View",
  },
  Cards: {
    name: "Cards Layouts",
    itemKey: "CardsLayouts",
    icon: "card-multiple-outline",
    viewName: "Cards View",
  },
  Board: {
    name: "Kanban Board Layouts",
    itemKey: "BoardLayouts",
    icon: "land-rows-vertical",
    viewName: "Kanban Board",
  },
  Timeline: {
    name: "Timeline Layouts",
    itemKey: "TimelineLayouts",
    icon: "timeline-clock-outline",
    viewName: "Timeline",
  },
  Calendar: {
    name: "Calendar Layout",
    itemKey: "CalendarConfig",
    icon: "calendar",
    viewName: "Calendar",
  },
};

/**
 * Manages TableColumn layouts via a Dropdown Button
 * For PageViewTable, the saved layout is the Fields inside the query.
 *
 * @param {string} layoutsType - The view type "Table" | "Cards" | "Board"
 * @param {function} onSave - Provide save function for Save As Default if you want to support Save As Default.
 * @param {function} onSelect - Callback function for when a layout is selected.
 * @param {Array} savedLayout - The current saved layout's Fields Array.
 * @param {string} savedLayoutID - The current saved layout's ID.
 * @param {Array} selectedLayout - The selected layout's Fields Array.
 * @param {string} selectedLayoutID - The id of the selected layout.
 * @param {object} currentModule - The current module the table's query is using.
 * @param {Array} layouts - All available layouts.
 * @param {function} createLayout - Callback function for when layout is created.
 * @param {function} updateLayout - Callback function for when a layout is updated.
 * @param {boolean} submitting - Whether or not create/updateLayout is submitting.
 * @param {function} deleteLayout - Callback function for when a layout is deleted.
 * @param {function} reorderLayouts- Callback function for when layouts are reordered
 * @param {function} validPermissions - Whether or not user has permissions to access to TableLayouts settings.
 * @param {CalendarConfig} calendarConfig - Configuration for Calendar
 * @param {function} onSaveCalendarConfig - For saving calendar configuration
 * */
const TableLayoutsButton = ({
  layoutsType = "Table",
  onSaveAsDefault,
  onSelect,
  savedLayout,
  savedLayoutID,
  selectedLayout,
  selectedLayoutName,
  selectedLayoutID,
  currentModule,
  layouts,
  submitting,
  createLayout,
  updateLayout,
  deleteLayout,
  reorderLayouts,
  validPermissions,
  field,
  onSaveAsGlobal,
  usePageSelection,
  handleAddGlobals,
  noWhisper,
  providedRef,
  calendarConfig,
  onSaveCalendarConfig,
}) => {
  const tableColumnsRefLocal = useRef();
  const tableColumnsRef = providedRef || tableColumnsRefLocal;
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  const [calendarDrawerOpen, setCalendarDrawerOpen] = useState(false);
  const [editLayout, setEditLayout] = useState(null); // The layout currently being edited
  const [open, setOpen] = useState(false);
  const { globals, globalsLoading } = useGlobals("Layouts");

  const layoutsWithGlobals = useMemo(() => {
    if (
      !onSaveAsGlobal ||
      !Array.isArray(layouts) ||
      !layouts?.length ||
      globalsLoading ||
      !globals
    ) {
      return layouts;
    }

    return layouts.flatMap((l) => {
      const globalID = l.GlobalID;

      if (globalID) {
        const globalLayout = _.findWhere(globals, { id: globalID });

        if (!globalLayout) return [];

        const { id, ...globalLayoutData } = globalLayout;

        return [{ ...globalLayoutData, ...l }];
      }

      return [l];
    });
  }, [layouts, globals, globalsLoading]);

  const layoutInfo = layoutTypeMap[layoutsType];

  if (layoutsType === "Calendar") {
    return {
      LayoutOptionsComponent: (
        <>
          {layouts?.length < 1 && (
            <>
              <div
                className="py-2 text-xs border border-superlight rounded px-2 mb-2 bg-ultralight text-dark"
                style={{ whiteSpace: "pre-wrap", color: "var(--medium)" }}
              >
                Add your first Layout in the settings below to get started.
              </div>
            </>
          )}
          <DropdownOptionWithIcon
            icon="mdi mdi-cog"
            name="Calendar Configuration"
            disabled={!validPermissions}
            onClick={() => {
              setCalendarDrawerOpen(true);
              tableColumnsRef.current?.close?.();
            }}
          />
        </>
      ),
      Drawers: (
        <CalendarLayoutDrawer
          open={calendarDrawerOpen}
          onClose={() => setCalendarDrawerOpen(false)}
          existingConfig={calendarConfig}
          moduleAPIName={currentModule?.APIName}
          saving={submitting}
          onSaveCalendarConfig={onSaveCalendarConfig}
        />
      ),
    };
  }

  if (!layoutInfo) {
    console.log("Error! Invalid layout type provided...");
    return { LayoutOptionsComponent: null, Drawers: null };
  }

  const LayoutOptionsComponent = (
    <LayoutsOptions
      title={layoutInfo.name}
      icon={layoutInfo.icon}
      openManage={() => {
        setManageDrawerOpen(true);
        tableColumnsRef.current?.close?.();
      }}
      onSelect={onSelect}
      layouts={layoutsWithGlobals}
      selectedLayout={selectedLayout}
      selectedLayoutID={selectedLayoutID}
      savedLayout={savedLayout}
      savedLayoutID={savedLayoutID}
      moduleAPIName={currentModule?.APIName}
      validPermissions={validPermissions}
      hideTitle={noWhisper}
    />
  );

  const Drawers = (
    <>
      <CreateTableLayoutDrawer
        open={createDrawerOpen}
        submitting={submitting}
        onClose={() => {
          setCreateDrawerOpen(false);
          setEditLayout(null);
          !manageDrawerOpen && tableColumnsRef?.current?.open?.();
        }}
        currentModule={currentModule}
        handleCreate={createLayout}
        handleUpdate={updateLayout}
        existingLayout={editLayout}
        fieldSchema={field}
        title={layoutsType}
        updateKey={layoutInfo.itemKey}
        limit={layoutsType === "Table" ? null : 6}
        existingLayouts={layouts}
        usePageSelection={usePageSelection}
        handleAddGlobals={handleAddGlobals}
        layoutT={layoutsType}
      />
      <ManageItemsDrawer
        open={manageDrawerOpen}
        onClose={() => {
          setManageDrawerOpen(false);
          tableColumnsRef?.current?.open?.();
        }}
        items={onSaveAsGlobal ? layoutsWithGlobals : layouts}
        onEdit={(val) => {
          setEditLayout(val);
          setCreateDrawerOpen(true);
        }}
        onDelete={deleteLayout}
        onReorder={reorderLayouts}
        title={layoutInfo.name}
        itemKey={layoutInfo.itemKey}
        savedItemID={savedLayoutID}
        onSaveAsDefault={onSaveAsDefault}
        headerDescription="Set the default, reorder, add, update, delete layouts"
        description="No layouts available. Get started by adding a new one."
        onSaveAsGlobal={onSaveAsGlobal}
        type={"Layout"}
      />
    </>
  );

  if (noWhisper)
    return {
      LayoutOptionsComponent,
      Drawers,
    };

  return (
    <>
      <ButtonGroup className="ml-0">
        <Whisper
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          placement="autoVerticalStart"
          trigger="click"
          ref={tableColumnsRef}
          preventOverflow
          speaker={
            <Popover
              className="sidebar-popover"
              style={{ width: "350px" }}
              full
            >
              {LayoutOptionsComponent}
            </Popover>
          }
          controlId="newRecordDropdown"
        >
          <DropdownButton
            icon={layoutInfo.icon}
            name={selectedLayoutName ?? "Default Layout"}
            open={open}
          />
        </Whisper>
      </ButtonGroup>

      {Drawers}
    </>
  );
};

export function DropdownButton({ open, name, icon }) {
  const mobileScreen = useAtomValue(IsMobileAtom);

  return (
    <Button
      size="sm"
      outline
      className={classNames("d-flex align-items-center btn-icon-only m-0 p-2", {
        "justify-content-between text-truncate": !mobileScreen,
        "justify-content-center": mobileScreen,
      })}
      style={{
        width: mobileScreen ? 32 : "auto",
        height: mobileScreen ? 36 : undefined,
        gap: "10px",
      }}
    >
      <i
        className={`mdi mdi-${icon} p-0 m-0`}
        style={{
          fontSize: mobileScreen ? 30 : 20,
        }}
      />
      {!mobileScreen && <span className="m-0 p-0">{name}</span>}
      {!mobileScreen && (
        <i
          className="mdi mdi-chevron-down m-0 p-0"
          style={{ fontSize: "20px" }}
        ></i>
      )}
    </Button>
  );
}

export default TableLayoutsButton;
